/**
 * DOMI-CARE - Almacenamiento Local Offline (IndexedDB con Fallback Seguro)
 * Proporciona soporte offline completo para profesionales en campo.
 * Mantiene la base de datos local y la cola de sincronización transaccional.
 */

import { SEED_DATA } from '../data/seedData.js';

const DB_NAME = 'DomiCareOfflineDB';
const DB_VERSION = 1;

class OfflineStore {
  constructor() {
    this.db = null;
    this.memoryFallback = false;
    this.initPromise = this.initDB();
  }

  async initDB() {
    return new Promise((resolve) => {
      try {
        if (!window.indexedDB) {
          console.warn('DOMI-CARE: IndexedDB no disponible, usando memoria/localStorage.');
          this.memoryFallback = true;
          this.initLocalStorageFallback();
          resolve(null);
          return;
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          
          const stores = [
            'pacientes', 'evoluciones', 'signos', 'escalas',
            'agenda', 'insumos', 'equipamiento', 'auditoria',
            'incidentes', 'settings'
          ];

          stores.forEach(name => {
            if (!db.objectStoreNames.contains(name)) {
              db.createObjectStore(name, { keyPath: name === 'settings' ? 'key' : 'id' });
            }
          });

          if (!db.objectStoreNames.contains('syncQueue')) {
            const qStore = db.createObjectStore('syncQueue', { keyPath: 'queueId', autoIncrement: true });
            qStore.createIndex('status', 'status', { unique: false });
          }
        };

        request.onsuccess = (event) => {
          const db = event.target.result;
          this.db = db;

          // Sembrar datos directamente usando la instancia `db` sin bloquear initPromise
          this.seedDatabaseDirect(db)
            .then(() => {
              resolve(db);
            })
            .catch((err) => {
              console.error('Error al sembrar datos iniciales:', err);
              resolve(db);
            });
        };

        request.onerror = (event) => {
          console.error('Error al abrir IndexedDB, activando fallback:', event.target.error);
          this.memoryFallback = true;
          this.initLocalStorageFallback();
          resolve(null);
        };
      } catch (err) {
        console.error('Excepción al inicializar IndexedDB:', err);
        this.memoryFallback = true;
        this.initLocalStorageFallback();
        resolve(null);
      }
    });
  }

  seedDatabaseDirect(db) {
    return new Promise((resolve) => {
      try {
        const tx = db.transaction('pacientes', 'readonly');
        const store = tx.objectStore('pacientes');
        const req = store.count();

        req.onsuccess = () => {
          if (req.result === 0) {
            console.log('DOMI-CARE: Inicializando datos semilla clínicos en IndexedDB...');
            const seedTx = db.transaction([
              'pacientes', 'agenda', 'evoluciones', 'signos', 'escalas',
              'insumos', 'equipamiento', 'auditoria', 'incidentes', 'settings'
            ], 'readwrite');

            const pStore = seedTx.objectStore('pacientes');
            SEED_DATA.pacientes.forEach(p => pStore.put(p));

            const aStore = seedTx.objectStore('agenda');
            SEED_DATA.agendaHoy.forEach(a => aStore.put(a));

            const eStore = seedTx.objectStore('evoluciones');
            SEED_DATA.evolucionesHCD.forEach(e => eStore.put(e));

            const sStore = seedTx.objectStore('signos');
            SEED_DATA.signosVitalesHistorico.forEach(s => sStore.put(s));

            const escStore = seedTx.objectStore('escalas');
            SEED_DATA.escalasHistorico.forEach(esc => escStore.put(esc));

            const iStore = seedTx.objectStore('insumos');
            SEED_DATA.insumosStock.forEach(i => iStore.put(i));

            const eqStore = seedTx.objectStore('equipamiento');
            SEED_DATA.equipamientoComodato.forEach(eq => eqStore.put(eq));

            const audStore = seedTx.objectStore('auditoria');
            SEED_DATA.auditoriaFacturacion.forEach(aud => audStore.put(aud));

            const incStore = seedTx.objectStore('incidentes');
            SEED_DATA.incidentes.forEach(inc => incStore.put(inc));

            const setStore = seedTx.objectStore('settings');
            setStore.put({ key: 'appsScriptUrl', value: '' });
            setStore.put({ key: 'mode', value: 'demo' });
            setStore.put({ key: 'activeUser', value: SEED_DATA.usuarios[0] });

            seedTx.oncomplete = () => resolve(true);
            seedTx.onerror = () => resolve(false);
          } else {
            resolve(true);
          }
        };

        req.onerror = () => resolve(false);
      } catch (e) {
        console.error('Error comprobando seed:', e);
        resolve(false);
      }
    });
  }

  initLocalStorageFallback() {
    if (!localStorage.getItem('domicare_seed_initialized')) {
      localStorage.setItem('domicare_pacientes', JSON.stringify(SEED_DATA.pacientes));
      localStorage.setItem('domicare_agenda', JSON.stringify(SEED_DATA.agendaHoy));
      localStorage.setItem('domicare_evoluciones', JSON.stringify(SEED_DATA.evolucionesHCD));
      localStorage.setItem('domicare_signos', JSON.stringify(SEED_DATA.signosVitalesHistorico));
      localStorage.setItem('domicare_escalas', JSON.stringify(SEED_DATA.escalasHistorico));
      localStorage.setItem('domicare_insumos', JSON.stringify(SEED_DATA.insumosStock));
      localStorage.setItem('domicare_equipamiento', JSON.stringify(SEED_DATA.equipamientoComodato));
      localStorage.setItem('domicare_auditoria', JSON.stringify(SEED_DATA.auditoriaFacturacion));
      localStorage.setItem('domicare_incidentes', JSON.stringify(SEED_DATA.incidentes));
      localStorage.setItem('domicare_seed_initialized', 'true');
    }
  }

  // ==========================================
  // OPERACIONES DE DATOS CON RESILIENCIA
  // ==========================================

  async getAll(storeName) {
    await this.initPromise;

    if (this.memoryFallback || !this.db) {
      const raw = localStorage.getItem(`domicare_${storeName}`);
      return raw ? JSON.parse(raw) : (SEED_DATA[storeName] || []);
    }

    return new Promise((resolve) => {
      try {
        const tx = this.db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const req = store.getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => {
          const raw = localStorage.getItem(`domicare_${storeName}`);
          resolve(raw ? JSON.parse(raw) : []);
        };
      } catch (e) {
        const raw = localStorage.getItem(`domicare_${storeName}`);
        resolve(raw ? JSON.parse(raw) : []);
      }
    });
  }

  async getById(storeName, key) {
    await this.initPromise;

    if (this.memoryFallback || !this.db) {
      const all = await this.getAll(storeName);
      return all.find(item => (storeName === 'settings' ? item.key === key : item.id === key)) || null;
    }

    return new Promise((resolve) => {
      try {
        const tx = this.db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const req = store.get(key);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = async () => {
          const all = await this.getAll(storeName);
          resolve(all.find(item => item.id === key) || null);
        };
      } catch (e) {
        resolve(null);
      }
    });
  }

  async put(storeName, item) {
    await this.initPromise;

    if (this.memoryFallback || !this.db) {
      const all = await this.getAll(storeName);
      const keyProp = storeName === 'settings' ? 'key' : 'id';
      const idx = all.findIndex(x => x[keyProp] === item[keyProp]);
      if (idx >= 0) all[idx] = item;
      else all.push(item);
      localStorage.setItem(`domicare_${storeName}`, JSON.stringify(all));
      return item[keyProp];
    }

    return new Promise((resolve) => {
      try {
        const tx = this.db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const req = store.put(item);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      } catch (e) {
        resolve(null);
      }
    });
  }

  async bulkInsert(storeName, items) {
    await this.initPromise;

    if (this.memoryFallback || !this.db) {
      localStorage.setItem(`domicare_${storeName}`, JSON.stringify(items));
      return true;
    }

    return new Promise((resolve) => {
      try {
        const tx = this.db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        items.forEach(item => store.put(item));
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
      } catch (e) {
        resolve(false);
      }
    });
  }

  async count(storeName) {
    await this.initPromise;

    if (this.memoryFallback || !this.db) {
      const all = await this.getAll(storeName);
      return all.length;
    }

    return new Promise((resolve) => {
      try {
        const tx = this.db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const req = store.count();
        req.onsuccess = () => resolve(req.result || 0);
        req.onerror = () => resolve(0);
      } catch (e) {
        resolve(0);
      }
    });
  }

  // Manejo de Cola de Sincronización (Offline Sync Queue)
  async enqueueAction(action, payload) {
    await this.initPromise;
    const queueItem = {
      action,
      payload,
      timestamp: new Date().toISOString(),
      status: 'pending',
      retryCount: 0
    };

    if (this.memoryFallback || !this.db) {
      const q = JSON.parse(localStorage.getItem('domicare_syncQueue') || '[]');
      queueItem.queueId = Date.now();
      q.push(queueItem);
      localStorage.setItem('domicare_syncQueue', JSON.stringify(q));
      window.dispatchEvent(new CustomEvent('domicare:queue-updated'));
      return queueItem.queueId;
    }

    return new Promise((resolve) => {
      try {
        const tx = this.db.transaction('syncQueue', 'readwrite');
        const store = tx.objectStore('syncQueue');
        const req = store.add(queueItem);
        req.onsuccess = () => {
          window.dispatchEvent(new CustomEvent('domicare:queue-updated'));
          resolve(req.result);
        };
        req.onerror = () => resolve(null);
      } catch (e) {
        resolve(null);
      }
    });
  }

  async getPendingQueue() {
    await this.initPromise;

    if (this.memoryFallback || !this.db) {
      const q = JSON.parse(localStorage.getItem('domicare_syncQueue') || '[]');
      return q.filter(it => it.status === 'pending');
    }

    return new Promise((resolve) => {
      try {
        const tx = this.db.transaction('syncQueue', 'readonly');
        const store = tx.objectStore('syncQueue');
        const req = store.getAll();
        req.onsuccess = () => {
          const items = (req.result || []).filter(it => it.status === 'pending');
          resolve(items);
        };
        req.onerror = () => resolve([]);
      } catch (e) {
        resolve([]);
      }
    });
  }

  async clearQueue() {
    await this.initPromise;

    if (this.memoryFallback || !this.db) {
      localStorage.setItem('domicare_syncQueue', '[]');
      window.dispatchEvent(new CustomEvent('domicare:queue-updated'));
      return true;
    }

    return new Promise((resolve) => {
      try {
        const tx = this.db.transaction('syncQueue', 'readwrite');
        const store = tx.objectStore('syncQueue');
        const req = store.clear();
        req.onsuccess = () => {
          window.dispatchEvent(new CustomEvent('domicare:queue-updated'));
          resolve(true);
        };
        req.onerror = () => resolve(false);
      } catch (e) {
        resolve(false);
      }
    });
  }

  async getSetting(key, defaultValue = null) {
    const record = await this.getById('settings', key);
    return record ? record.value : defaultValue;
  }

  async setSetting(key, value) {
    return this.put('settings', { key, value });
  }
}

export const offlineStore = new OfflineStore();
